import axios from 'axios';

export const METHOD = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  DELETE: 'delete',
  PATCH: 'patch',
};

let headers = {
  Accept: 'application/json',
  Authorization: '',
};

export const client = axios.create();

// ---- Global API logging (request/response/error) ----
const maskSensitive = (value, key = '') => {
  const k = `${key}`.toLowerCase();
  if (k.includes('authorization') || k.includes('token')) return '[REDACTED]';
  if (k.includes('password') || k === 'pwd') return '[REDACTED]';
  if (typeof value === 'string' && value.length > 2000) return `${value.slice(0, 2000)}…[truncated]`;
  return value;
};

const safeJson = (obj) => {
  try {
    return JSON.parse(JSON.stringify(obj, (k, v) => maskSensitive(v, k)));
  } catch (e) {
    return '[unserializable]';
  }
};

const containsKeyDeep = (obj, key, maxDepth = 6) => {
  try {
    const seen = new Set();
    const stack = [{ value: obj, depth: 0 }];
    while (stack.length > 0) {
      const { value, depth } = stack.pop();
      if (value === null || value === undefined) continue;
      if (typeof value !== 'object') continue;
      if (seen.has(value)) continue;
      seen.add(value);

      if (Object.prototype.hasOwnProperty.call(value, key)) return true;
      if (depth >= maxDepth) continue;

      if (Array.isArray(value)) {
        for (let i = 0; i < value.length; i++) {
          stack.push({ value: value[i], depth: depth + 1 });
        }
      } else {
        for (const k of Object.keys(value)) {
          stack.push({ value: value[k], depth: depth + 1 });
        }
      }
    }
  } catch (_) {
    // ignore
  }
  return false;
};

client.interceptors.request.use(
  (config) => {
    config.metadata = { startTime: Date.now() };
    const logConfig = {
      method: config.method,
      url: config.url,
      timeout: config.timeout,
      params: config.params,
      data: config.data instanceof FormData ? '[FormData]' : config.data,
      headers: config.headers,
    };
    console.info('[HTTP][REQ]', safeJson(logConfig));
    return config;
  },
  (error) => {
    console.error('[HTTP][REQ][ERR]', safeJson({ message: error?.message, error }));
    return Promise.reject(error);
  },
);

client.interceptors.response.use(
  (response) => {
    const start = response?.config?.metadata?.startTime;
    const durationMs = start ? Date.now() - start : undefined;
    const log = {
      method: response?.config?.method,
      url: response?.config?.url,
      status: response?.status,
      durationMs,
      contains_bleSupported: containsKeyDeep(response?.data, 'bleSupported'),
      data: response?.data,
    };
    console.info('[HTTP][RES]', safeJson(log));
    return response;
  },
  (error) => {
    const start = error?.config?.metadata?.startTime;
    const durationMs = start ? Date.now() - start : undefined;
    const log = {
      method: error?.config?.method,
      url: error?.config?.url,
      status: error?.response?.status,
      durationMs,
      contains_bleSupported: containsKeyDeep(error?.response?.data, 'bleSupported'),
      response: error?.response?.data,
      message: error?.message,
    };
    console.error('[HTTP][RES][ERR]', safeJson(log));
    return Promise.reject(error);
  },
);

const request = (method, url, args, isBlob = false, config = {}) => {
  let params = {};
  if (method === METHOD.GET) {
    params = { params: args };
  } else {
    params = { data: args };
  }
  if (!args) {
    params = {};
  }
  if (isBlob) {
    params = { ...params, responseType: 'blob' };
  }

  const axiosConfig = {
    headers: headers,
    method,
    url,
    timeout: 10000,
    ...params,
    ...config,
  };

  // keep legacy config log minimal; detailed logs are handled by interceptors above
  console.info('[HTTP][CFG]', safeJson({
    method: axiosConfig.method,
    url: axiosConfig.url,
    timeout: axiosConfig.timeout,
    params: axiosConfig.params,
    data: axiosConfig.data instanceof FormData ? '[FormData]' : axiosConfig.data,
  }));
  return client(axiosConfig);
};

const requestFunc = async (method, url, args, isBlob = false, config = {}) => {
  const response = await request(
    method,
    `${process.env.VUE_APP_PROXY_DOMAIN}/api${url}`,
    args,
    isBlob,
    config,
  );
  // console.info('response.data:', JSON.stringify(response?.data));
  return response.data;
};

const requestAuthFunc = async (method, url, args, isBlob = false, config = {}) => {
  const response = await request(
    method,
    `${process.env.VUE_APP_PROXY_DOMAIN}/api${url}`,
    args,
    isBlob,
    config,
  );
  // console.info('response.data:', JSON.stringify(response?.data));
  return response.data;
};

const requestSmartHomeFunc = async (method, url, args, isBlob = false, config = {}) => {
  const response = await request(
    method,
    `${process.env.VUE_APP_PROXY_DOMAIN}/api${url}`,
    args,
    isBlob,
    config,
  );
  // console.info('response.data:', JSON.stringify(response?.data));
  return response.data;
};

const invokeRequestFunc = async (method, url, params, type = '', config = {}) => {
  if (type === 'auth') {
    return await requestAuthFunc(method, url, params, false, config);
  } else if (type === 'smarthome') {
    return await requestSmartHomeFunc(method, url, params, false, config);
  }
  return await requestFunc(method, url, params, false, config);
};

const Axios = {
  get: async (url, params, type = '', config = {}) => {
    return await invokeRequestFunc(METHOD.GET, url, params, type, config);
  },
  post: async (url, params, type = '', config = {}) => {
    return await invokeRequestFunc(METHOD.POST, url, params, type, config);
  },
  put: async (url, params, type = '', config = {}) => {
    return await invokeRequestFunc(METHOD.PUT, url, params, type, config);
  },
  patch: async (url, params, type = '', config = {}) => {
    return await invokeRequestFunc(METHOD.PATCH, url, params, type, config);
  },
  delete: async (url, params, type = '', config = {}) => {
    return await invokeRequestFunc(METHOD.DELETE, url, params, type, config);
  },
  setToken: (token, clear = false) => {
    if (clear) {
      headers.Authorization = '';
    } else {
      headers.Authorization = `Bearer ${token}`;
    }
  },
  clearToken: () => (headers.Authorization = ''),
};

export default Axios;
export * from './apiUrls';
